"use client"

import React, { useEffect, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import AuthBgWrapper from '@/features/auth/components/auth-bg-wrapper'
import AuthCardWrapper from '@/features/auth/components/auth-card-wrapper'
import { loginScreenBgImage } from '@/features/auth/constants/image'
import { Button } from '@/shared/components/ui/button'
import { useRouter } from 'next/navigation'
import { AUTH_PAGE_ROUTE } from '@/shared/constants/pages-routes'

function RoleSelectionPageContent() {
    const router = useRouter()
    const searchParams = useSearchParams()

    // Check for OAuth session token from URL (when redirected from backend)
    useEffect(() => {
        const oauthSession = searchParams.get('oauthSession')
        const stateParam = searchParams.get('state')
        
        if (oauthSession) {
            // Store OAuth session token for use in complete-oauth page
            sessionStorage.setItem('oauthPendingSessionToken', oauthSession)
            console.log('[RolePage] Stored OAuth session token from URL:', oauthSession)
        }
        
        // Parse state parameter to restore URL params (region, team, etc.)
        if (stateParam) {
            try {
                const parts = stateParam.split('|')
                if (parts.length > 1) {
                    const params = parts[1]
                    const urlParams = new URLSearchParams(params)
                    
                    const region = urlParams.get('region')
                    const team = urlParams.get('team')
                    
                    if (region) sessionStorage.setItem('unifiedAuthRegion', region)
                    if (team) sessionStorage.setItem('unifiedAuthTeam', team)
                    
                    console.log('[RolePage] Restored URL params from state:', { region, team })
                }
            } catch (error) {
                console.error('[RolePage] Failed to parse state parameter:', error)
            }
        }
    }, [searchParams])

    const handleRoleSelection = (role: 'volunteer' | 'homeowner') => {
        // Store selected role in sessionStorage
        sessionStorage.setItem('registrationRole', role)
        sessionStorage.setItem('unifiedAuthRole', role)
        
        // Check if this is an OAuth flow (has pending session token)
        const oauthPendingSessionToken = sessionStorage.getItem('oauthPendingSessionToken')
        
        if (oauthPendingSessionToken) {
            // OAuth flow: route to complete-oauth with role
            // The complete-oauth page will handle the registration based on role
            router.push(`/auth/complete-oauth?session=${oauthPendingSessionToken}&role=${role}`)
        } else {
            // Email registration flow: route to email-registration with role parameter
            router.push(`/email-registration?role=${role}`)
        }
    }

    return (
        <AuthBgWrapper image={loginScreenBgImage}>
            <AuthCardWrapper
                headerTitle="Choose Your Role"
                headerDescription="Please select whether you are registering as a volunteer or a homeowner."
            >
                <div className="space-y-3 w-full pb-6">
                    <Button
                        type="button"
                        className="w-full"
                        onClick={() => handleRoleSelection('volunteer')}
                    >
                        Register as a Volunteer
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => handleRoleSelection('homeowner')}
                    >
                        Register as a Homeowner
                    </Button>
                </div>
            </AuthCardWrapper>
        </AuthBgWrapper>
    )
}

function RoleSelectionPage() {
    return (
        <Suspense fallback={
            <AuthBgWrapper image={loginScreenBgImage}>
                <AuthCardWrapper
                    headerTitle="Choose Your Role"
                    headerDescription="Please select whether you are registering as a volunteer or a homeowner."
                >
                    <div className="flex items-center justify-center min-h-[200px]">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                            <p className="text-muted-foreground">Loading...</p>
                        </div>
                    </div>
                </AuthCardWrapper>
            </AuthBgWrapper>
        }>
            <RoleSelectionPageContent />
        </Suspense>
    )
}

export default RoleSelectionPage


